import { parseEnvx } from "./parser";
import { validateEnv } from "./validator";
import { EnvxSchema, EnvxResult, ParsedLine } from "../types";
import { resolveEnvx } from "./resolver";
import { fillMissingSchemaEntries } from "../utils";
import { TERNARY_REGEX } from "../regex";

export function loadEnvxFromString(
  content: string,
  schema?: EnvxSchema,
  noValidate?: boolean
): { result: EnvxResult; schema?: EnvxSchema; rawEnv: ParsedLine[] } {
  const { env, schema: parsedSchema } = parseEnvx(content);

  const fullSchema = fillMissingSchemaEntries(env, parsedSchema);

  const effectiveSchema: EnvxSchema | undefined = schema
    ? { ...fullSchema, ...schema }
    : Object.keys(fullSchema).length > 0
    ? fullSchema
    : undefined;

  const resolved = resolveEnvx(env, effectiveSchema);

  /**
   * Filter out variables that are not explicitly defined in the effective schema.
   *
   * This is important because some variables (like system environment variables
   * such as NODE_ENV) may be injected at runtime (e.g., via process.env)
   * and resolved correctly by `resolveEnvx`, even if the user did not define them
   * in the .envx file or the schema.
   *
   * By filtering them out here, we ensure that only user-declared variables
   * are included in the final result. This avoids polluting the generated output
   * with runtime-only values that are not relevant to the declared configuration.
   *
   * Note: These values can still be *used* (e.g., in interpolations or ternaries),
   * but they won't appear in the final output unless explicitly declared in schema.
   */
  const filteredResult = Object.fromEntries(
    Object.entries(resolved).filter(
      ([key]) => effectiveSchema?.[key] !== undefined
    )
  );

  if (effectiveSchema && !noValidate) {
    const validatedEnvs = validateEnv(
      Object.entries(filteredResult).map(([key, value]) => ({ key, value })),
      effectiveSchema
    );

    return { result: validatedEnvs, schema: effectiveSchema, rawEnv: [] };
  }

  return { result: filteredResult, schema: effectiveSchema, rawEnv: env };
}

export function isTernary(str: string): boolean {
  const cleaned = str.trim();
  return TERNARY_REGEX.test(cleaned);
}
export function hasInterpolation(str: string): boolean {
  return typeof str === "string" && str.includes("${");
}
